import type { SegmentProposal } from '@/core/data/spark/spark.interface';
import { gambits } from '@/core/gambits';
import { createLogger } from '@/core/helpers/logger.helper';
import { firestore } from '@/core/network/firebase';
import type { Result } from '@/core/network/http/httpClient.interface';
import { addDays } from 'date-fns';
import { doc, getDoc, onSnapshot, Timestamp, type Unsubscribe } from 'firebase/firestore';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useSparkStore } from './spark.store';

const logger = createLogger('SparkProposalStore');

interface SparkProposalFirestore {
  id: string;
  updatedAt: Timestamp;
  currentSubscribers?: number;
  deleted?: boolean;
  mergedIntoGambitId?: string;
}

export const useSparkProposalStore = defineStore('sparkProposal', () => {
  const proposalId = ref<string | null>(null);
  const sparkProposal = ref<SegmentProposal | null>(null);
  const isProposalLoading = ref<boolean>(false);
  const isProposalError = ref<boolean>(false);
  const isProposalNotFound = ref<boolean>(false);
  const mergedIntoGambitId = ref<string | null>(null);
  const unsubscribeFromProposal = ref<Unsubscribe | null>(null);
  const inFlightProposalFetches = new Map<string, Promise<Result<SegmentProposal> | undefined>>();
  let liveProposalWriteGeneration = 0;

  // Fetching

  let didExist = false;

  const clearProposalStore = () => {
    if (unsubscribeFromProposal.value !== null) {
      unsubscribeFromProposal.value();
      unsubscribeFromProposal.value = null;
    }

    proposalId.value = null;
    isProposalLoading.value = false;
    isProposalError.value = false;
    isProposalNotFound.value = false;
    mergedIntoGambitId.value = null;
    sparkProposal.value = null;
    inFlightProposalFetches.clear();
    liveProposalWriteGeneration += 1;
  };

  const applyLiveProposal = (proposal: SegmentProposal) => {
    liveProposalWriteGeneration += 1;
    inFlightProposalFetches.delete(proposal.id);
    proposalId.value = proposal.id;
    sparkProposal.value = proposal;
    isProposalNotFound.value = false;
    isProposalError.value = false;
  };

  const subscribeToProposal = (id: string) => {
    if (unsubscribeFromProposal.value !== null) {
      unsubscribeFromProposal.value();
      unsubscribeFromProposal.value = null;
    }

    didExist = false;

    const docRef = doc(firestore, 'proposals', id);

    unsubscribeFromProposal.value = onSnapshot(
      docRef,
      async (docSnap) => {
        if (docSnap.exists()) {
          logger.info(`Proposal ${id} Firestore doc found.`);
          didExist = true;
          const data = docSnap.data() as SparkProposalFirestore;

          if (data.deleted && data.mergedIntoGambitId) {
            logger.info(`Proposal ${id} was merged into gambit ${data.mergedIntoGambitId}.`);
            mergedIntoGambitId.value = data.mergedIntoGambitId;
            isProposalNotFound.value = true;
            sparkProposal.value = null;

            const sparkStore = useSparkStore();
            sparkStore.mergeProposalIntoGambit(id, data.mergedIntoGambitId);
            sparkStore.fetchIntents({ refreshExisting: true });
            sparkStore.findMyGambits(new Date(), addDays(new Date(), 7));
            return;
          }

          const firestoreTs = data.updatedAt.toDate().getTime();
          const localTs = sparkProposal.value?.updatedAt ? new Date(sparkProposal.value.updatedAt).getTime() : 0;
          const willFetch = !sparkProposal.value?.updatedAt || firestoreTs > localTs;

          if (willFetch) {
            const prevSuccess = sparkProposal.value?.isSuccessful;
            await fetchProposal(docSnap.id);
            if (!prevSuccess && sparkProposal.value?.isSuccessful) {
              const sparkStore = useSparkStore();
              sparkStore.fetchIntents({ refreshExisting: true });
              sparkStore.findMyGambits(new Date(), addDays(new Date(), 7));
            }
          }
        } else if (didExist) {
          logger.info(`Proposal ${id} Firestore doc deleted.`);
          isProposalNotFound.value = true;
          sparkProposal.value = null;
        } else {
          logger.warn(`Proposal ${id} Firestore doc not found. Creating.`);
          gambits.sparkService.createFirestoreProposalDoc(id);
        }
      },
      (error) => {
        logger.error(`Proposal ${id} onSnapshot listener error: ${error.message} (${error.code})`);
      },
    );
  };

  const fetchProposal = async (id: string): Promise<Result<SegmentProposal> | undefined> => {
    const inFlightFetch = inFlightProposalFetches.get(id);

    if (inFlightFetch) {
      return inFlightFetch;
    }

    const fetchGeneration = liveProposalWriteGeneration;
    const pendingFetch: { promise?: Promise<Result<SegmentProposal> | undefined> } = {};

    pendingFetch.promise = (async (): Promise<Result<SegmentProposal> | undefined> => {
      proposalId.value = id;
      isProposalLoading.value = true;
      isProposalError.value = false;
      isProposalNotFound.value = false;

      const isRefresh = sparkProposal.value?.id === id;

      if (!isRefresh) {
        sparkProposal.value = null;
      }

      try {
        const result = await gambits.sparkService.getProposal(id);

        if (result?.data && fetchGeneration === liveProposalWriteGeneration) {
          sparkProposal.value = result.data;

          if (!isRefresh) {
            subscribeToProposal(sparkProposal.value.id);
          }
        } else if (result?.error?.code === 404) {
          isProposalNotFound.value = true;
          sparkProposal.value = null;

          try {
            const firestoreDoc = await getDoc(doc(firestore, 'proposals', id));
            const data = firestoreDoc.exists() ? (firestoreDoc.data() as SparkProposalFirestore) : null;
            if (data?.deleted && data.mergedIntoGambitId) {
              mergedIntoGambitId.value = data.mergedIntoGambitId;
              useSparkStore().mergeProposalIntoGambit(id, data.mergedIntoGambitId);
            }
          } catch {
            logger.warn(`Could not check Firestore for merged proposal ${id}.`);
          }
        } else {
          isProposalError.value = true;
        }

        return result;
      } finally {
        isProposalLoading.value = false;
        if (inFlightProposalFetches.get(id) === pendingFetch.promise) {
          inFlightProposalFetches.delete(id);
        }
      }
    })();

    inFlightProposalFetches.set(id, pendingFetch.promise);
    return pendingFetch.promise;
  };

  return {
    clearProposalStore,
    fetchProposal,
    applyLiveProposal,
    unsubscribeFromProposal,

    sparkProposal,
    isProposalNotFound,
    isProposalError,
    proposalId,
    mergedIntoGambitId,
  };
});
